import { useCallback, useEffect, useState } from 'react';
import { safeGetJSON, safeSetJSON } from '../utils/safeStorage';
import { hasCheckinToday } from '../utils/checkinDay';

/**
 * Sistema de "bônus diário surpresa" — variable reward (loot box leve).
 *
 * Na primeira abertura do app de cada dia, o user pode resgatar X Six Coins.
 * O valor é aleatório (pesos abaixo), e a animação compõe com a streak.
 *
 * Por que variable reward funciona (resumo):
 *  - Recompensa fixa (sempre 10 coins): vira chato, expectativa baixa
 *  - Recompensa variável (5/10/20/50 random): dopamine spike a cada vez
 *  - Princípio Skinner box / slot machine sem ser predatório
 *
 * Pesos:
 *  - 10 coins: 65% (piso garantido — gera baseline de hábito)
 *  - 20 coins: 22% (bom — celebra)
 *  - 30 coins: 10% (épico)
 *  - 50 coins:  3% (raro — lendário, gera "wow")
 *
 * Piso de 10 escolhido pra cada visita diária ter peso percebível. Os
 * raros (30/50) seguram a variable reward.
 *
 * Persistência em localStorage (mesmo motivo do streak). Pra produção,
 * mover pra Supabase pra evitar farm (limpar storage → resgatar de novo).
 *
 * NOTA: o "ganho" em si depende do consumidor chamar `claim()`. Esse hook
 * só decide SE pode ganhar hoje, qual o valor (sorteado uma vez por dia),
 * e marca como resgatado. Quem credita os pontos é o consumidor (App.tsx
 * via updateUserProfile).
 */

interface DailyBonusData {
  /** ISO date (YYYY-MM-DD) do último dia em que o bônus foi resgatado. */
  lastClaimedDate: string | null;
  /**
   * Valor sorteado pra hoje. Só preenchido quando o user "abre" o modal
   * (ainda não confirmou). Garante que se ele fechar e voltar, vê o mesmo
   * número. Reset todo dia.
   */
  pendingTodayDate: string | null;
  pendingTodayAmount: number | null;
}

interface DailyBonusStatus {
  /** Pode resgatar hoje? (não resgatou ainda) */
  available: boolean;
  /** Valor sorteado pra esse resgate (se available, senão null). */
  amount: number | null;
  /**
   * True quando o valor sorteado é épico/lendário (≥20) E o user ainda não
   * fez check-in hoje. Nesse caso o modal mostra "preview locked" + CTA
   * pra fazer check-in. Comum (10) nunca requer check-in.
   */
  requiresCheckin: boolean;
  /** Resgata: marca como claimed e retorna o valor pra creditar. Idempotente. */
  claim: () => number | null;
}

const STORAGE_KEY = '@sixpass:daily-bonus';
const DEFAULT: DailyBonusData = {
  lastClaimedDate: null,
  pendingTodayDate: null,
  pendingTodayAmount: null,
};

function todayLocalISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

/** Sorteia o valor de hoje baseado nos pesos. */
function rollAmount(): number {
  const r = Math.random();
  if (r < 0.65) return 10; // piso — 65% das vezes
  if (r < 0.87) return 20; // 22%
  if (r < 0.97) return 30; // 10% — épico
  return 50; // 3% — lendário
}

export function useDailyBonus(): DailyBonusStatus {
  const [status, setStatus] = useState<DailyBonusStatus>({
    available: false,
    amount: null,
    requiresCheckin: false,
    claim: () => null,
  });

  const recompute = useCallback(() => {
    const today = todayLocalISO();
    const data = safeGetJSON<DailyBonusData>(STORAGE_KEY, DEFAULT);

    // Já resgatou hoje? Esconde.
    if (data.lastClaimedDate === today) {
      setStatus({
        available: false,
        amount: null,
        requiresCheckin: false,
        claim: () => null,
      });
      return;
    }

    // Sorteia (se ainda não sorteou hoje)
    let amount = data.pendingTodayAmount;
    if (data.pendingTodayDate !== today || amount === null) {
      amount = rollAmount();
      safeSetJSON(STORAGE_KEY, {
        ...data,
        pendingTodayDate: today,
        pendingTodayAmount: amount,
      });
    }

    // Daily Bonus B: épico/lendário (≥20) requer check-in do dia pra liberar.
    // Comum (10) é sempre liberado — protege a mecânica de "abrir = ganhar".
    const requiresCheckin = amount >= 20 && !hasCheckinToday();

    const claim = (): number | null => {
      const current = safeGetJSON<DailyBonusData>(STORAGE_KEY, DEFAULT);
      // Defensivo: se já resgatou (chamadas duplicadas), retorna null.
      if (current.lastClaimedDate === today) return null;
      // Defensivo: se requer check-in e ainda não tem, bloqueia o claim
      // (o UI deveria nem mostrar o botão, mas guard extra evita bug).
      const currentAmount = current.pendingTodayAmount ?? amount ?? 10;
      if (currentAmount >= 20 && !hasCheckinToday()) return null;
      safeSetJSON(STORAGE_KEY, {
        ...current,
        lastClaimedDate: today,
        // Limpa pendente — não vai mais aparecer hoje.
        pendingTodayDate: null,
        pendingTodayAmount: null,
      });
      setStatus({
        available: false,
        amount: null,
        requiresCheckin: false,
        claim: () => null,
      });
      return currentAmount;
    };

    setStatus({ available: true, amount, requiresCheckin, claim });
  }, []);

  useEffect(() => {
    recompute();
    // Re-recompute quando o user faz check-in (dispara o evento custom em
    // checkinDay.ts). Sem isso, o user faria check-in, voltaria pra Home,
    // e o modal continuaria locked porque hasCheckinToday foi avaliado no mount.
    if (typeof window === 'undefined') return;
    const onCheckin = () => recompute();
    window.addEventListener('sixpass:checkin-done', onCheckin);
    return () => window.removeEventListener('sixpass:checkin-done', onCheckin);
  }, [recompute]);

  return status;
}
